import { toast } from 'react-toastify';
import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { setPageTitle } from 'store/actions/header';
import { Button } from 'app/shared/button';
import { SimpleInput } from 'app/shared/input';
import { addOneAutomationToStore } from 'store/actions/automation';
import { DetailItem } from 'app/shared/info-section/item';
import useValidator from 'hooks/validator';
import { Spacer } from 'app/layouts/generic';
import { BasicInfoSection } from 'app/shared/info-section/basic';
import { TextArea } from 'app/shared/input/textarea';
import { useAutomationTemplateService } from 'hooks/automation/template';
import { PermissionsContext } from 'contexts/permissions';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { useEventsService } from 'hooks/automation/event';
import { MultiSelect } from 'app/shared/select';
import { GridColumn, GridRow } from 'app/layouts/grid';

const AutomationCreationForm = ({ setAutomationDataId }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useNavigate();
  const { tenant_id } = useSelector((state) => state.user_data);
  const { permissions } = useContext(PermissionsContext);
  const { createTemplate, fetchTemplate, updateTemplate } = useAutomationTemplateService();
  const { fetchEvents } = useEventsService();
  const { determineSubscriptionPlanClass } = useSubscriptionService();

  const [automation_id, setAutomationId] = useState(0);
  const [can_track_conversion, setCanTrackConversion] = useState(false);
  const [data, setData] = useState({ title: '', description: '' });
  const [events, setEvents] = useState([]);
  const [form_data, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected_event, setSelectedEvent] = useState({});

  const automationValidationConfig = {
    title: {
      required: true
    }
  };

  const { errors, validateField, form_is_valid } = useValidator(automationValidationConfig);

  useEffect(() => {
    determineSubscriptionPlanClass({
      query_string: 'plan_code=campaign,startup,automation_plus&status=active'
    }).then((plan_class) => {
      setCanTrackConversion(plan_class && plan_class !== 'flex');
    });
  }, [tenant_id]);

  useEffect(() => {
    if (isNaN(id)) return;
    setAutomationId(() => id);
    Promise.all([fetchEvents({ query_string: 'type=user' }), fetchTemplate(id)]).then(
      ([{ events }, { template }]) => {
        setData(template);
        const parsed_events = events.map((event) => ({
          ...event,
          value: event.code,
          label: event.name
        }));
        if (template.conversion_event) {
          setSelectedEvent(
            parsed_events.find((event) => event.code === template.conversion_event) || {}
          );
        }
        handleSimpleInfoChange('title', template.title);
        handleSimpleInfoChange('description', template.description);
      }
    );
  }, [id]);

  useEffect(() => {
    dispatch(
      setPageTitle([
        { title: 'Automations', path: '/automations' },
        {
          title: isNaN(id) ? 'Create Automation' : 'Update Automation',
          path: isNaN(id) ? '/automations/new' : `/automations/${id}`
        }
      ])
    );
    fetchEvents({ query_string: 'type=user' }).then(({ events }) => {
      setEvents(events.map((event) => ({ ...event, value: event.code, label: event.name })));
    });
  }, []);

  const handleSimpleInfoChange = (key, value = '') => {
    validateField(key, value);
    setFormData((curr_data) => ({ ...curr_data, [key]: value }));
  };

  const submitForm = async () => {
    const values = {
      ...form_data,
      conversion_event: selected_event?.value || '',
      tenant_id
    };

    setLoading(true);
    const { template } = automation_id
      ? await updateTemplate(automation_id, { data: values })
      : await createTemplate({ data: values });
    setLoading(false);

    if (!template) return;

    dispatch(addOneAutomationToStore({ ...data, ...template }));
    setAutomationDataId(template.id || automation_id);
    toast.success(`Automation ${automation_id ? 'updated' : 'created'} successfully`);

    if (!automation_id && template.id) {
      history(`/editor/automations/${template.id}`);
    }
  };

  return (
    <BasicInfoSection title="Basic details">
      <div>
        <GridRow num_of_columns={1}>
          <GridColumn>
            <DetailItem title="Title">
              <SimpleInput
                input_id="title"
                value={form_data.title}
                onInput={(e) => handleSimpleInfoChange('title', e)}
                required
                error={errors.title}
              />
            </DetailItem>
          </GridColumn>
        </GridRow>
        <GridRow num_of_columns={1}>
          <GridColumn>
            <DetailItem title="Description">
              <TextArea
                input_id="description"
                value={form_data.description}
                onInput={(e) => handleSimpleInfoChange('description', e)}
                rows={3}
              />
            </DetailItem>
          </GridColumn>
        </GridRow>
        {can_track_conversion && (
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem title="Conversion Event">
                <MultiSelect
                  value={selected_event}
                  onChange={setSelectedEvent}
                  options={events}
                  isClearable
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
        )}
        <Spacer multiple={2} />
        <Button
          icon_name="check_circle"
          onClick={submitForm}
          text={automation_id ? 'Update' : 'Save'}
          loading={loading}
          disabled={!form_is_valid || loading || !permissions['automation:update']}
        />
      </div>
    </BasicInfoSection>
  );
};

export default AutomationCreationForm;
